'use client';

import React, { useMemo } from 'react';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useGetReferralVolumeTrendsQuery } from 'src/api/redux';
import PreviewYoYRow from 'src/components/Profiles/shared/widgets/shared/PreviewYoYRow';
import PreviewInlineBar from 'src/components/Profiles/shared/widgets/shared/PreviewInlineBar';
import { formatNumber } from 'src/utils/formatNumber/formatNumber';

const PREVIEW_MAX_ITEMS = 3;

const ReferralVolumeTrendsPreview: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;

  const entityId = entityDetails?.id;
  const showPercentChange = widget?.show_percent_change !== false;
  const direction = widget?.direction;
  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;
  const dateRange =
    minYear && maxYear
      ? { gte: String(minYear), lte: String(maxYear) }
      : undefined;

  const codeSets = useMemo(() => {
    const sets =
      widget?.code_sets?.length ? widget.code_sets
      : widget?.code_set ? [widget.code_set]
      : [];
    const encs =
      widget?.encounters?.length ? widget.encounters
      : widget?.encounter ? [widget.encounter]
      : [];
    return [...sets, ...encs];
  }, [widget?.code_sets, widget?.code_set, widget?.encounters, widget?.encounter]);

  const q0 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[0]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[0] || !direction }
  );
  const q1 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[1]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[1] || !direction }
  );
  const q2 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[2]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[2] || !direction }
  );
  const q3 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[3]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[3] || !direction }
  );
  const q4 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[4]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[4] || !direction }
  );
  const q5 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[5]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[5] || !direction }
  );
  const q6 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[6]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[6] || !direction }
  );
  const q7 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[7]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[7] || !direction }
  );

  const queryData = useMemo(
    () => [q0.data, q1.data, q2.data, q3.data, q4.data, q5.data, q6.data, q7.data],
    [q0.data, q1.data, q2.data, q3.data, q4.data, q5.data, q6.data, q7.data]
  );
  const isLoading = [q0, q1, q2, q3, q4, q5, q6, q7].some((q, i) => codeSets[i] && q.isLoading);

  const trendItems = useMemo(() => {
    const items: {
      key: number;
      codeSetLabel: string;
      latestYear: string;
      latestValue: number;
      pctChange: number;
      isNew: boolean;
    }[] = [];

    codeSets.forEach((cs, i) => {
      const data = queryData[i];
      if (!data?.claims_trends) return;

      const years = Object.keys(data.claims_trends).sort();
      // Percent change needs a prior year to compare against; volume only needs one
      if (years.length < (showPercentChange ? 2 : 1)) return;

      const currentYear = years[years.length - 1];
      const previousYear = years[years.length - 2];
      const currentValue = Number(data.claims_trends[currentYear] ?? 0);
      const previousValue = previousYear
        ? Number(data.claims_trends[previousYear] ?? 0)
        : 0;

      items.push({
        key: cs.id,
        codeSetLabel: cs.label ?? 'Claims',
        latestYear: currentYear,
        latestValue: currentValue,
        pctChange: previousValue > 0
          ? Math.round(((currentValue - previousValue) / previousValue) * 100)
          : 0,
        isNew: previousValue === 0 && currentValue > 0,
      });
    });

    return items;
  }, [codeSets, queryData, showPercentChange]);

  if (isLoading || widgetConfig?.isLoading) {
    return <WidgetLoading data-testid="referral-volume-trends-preview-loading" aria-label="Loading referral volume trends" />;
  }

  if (trendItems.length === 0) {
    return <WidgetEmpty data-testid="referral-volume-trends-preview-empty" />;
  }

  if (!showPercentChange) {
    if (trendItems.length === 1) {
      const item = trendItems[0];
      return (
        <div
          className="flex flex-col gap-1"
          data-testid="referral-volume-trends-preview-volume-hero"
        >
          <span
            className="text-4xl font-bold tabular-nums text-sky-600"
            data-testid="referral-volume-trends-preview-volume-hero-value"
          >
            {formatNumber(item.latestValue)}
          </span>
          <span
            className="text-sm font-semibold text-neutral-500"
            data-testid="referral-volume-trends-preview-volume-hero-label"
          >
            {item.codeSetLabel} · {item.latestYear}
          </span>
        </div>
      );
    }

    const volumeItems = trendItems.slice(0, PREVIEW_MAX_ITEMS);
    const maxValue = Math.max(...volumeItems.map((item) => item.latestValue), 0);

    return (
      <div
        className="flex flex-col gap-1.5"
        data-testid="referral-volume-trends-preview-volume"
      >
        {volumeItems.map((item) => (
          <PreviewInlineBar
            key={item.key}
            label={`${item.codeSetLabel} · ${item.latestYear}`}
            value={item.latestValue}
            maxValue={maxValue}
            formattedValue={formatNumber(item.latestValue)}
          />
        ))}
        {trendItems.length > PREVIEW_MAX_ITEMS && (
          <span
            className="text-xs text-neutral-500"
            data-testid="referral-volume-trends-preview-overflow"
          >
            +{trendItems.length - PREVIEW_MAX_ITEMS} more
          </span>
        )}
      </div>
    );
  }

  if (trendItems.length === 1) {
    const item = trendItems[0];
    const isPositive = item.isNew || item.pctChange > 0;
    const isNegative = item.pctChange < 0;
    const arrowColor = isPositive
      ? 'text-green-600'
      : isNegative
        ? 'text-red-600'
        : 'text-neutral-500';
    const arrow = isPositive ? '▲' : isNegative ? '▼' : '—';
    const pctDisplay = item.isNew ? 'New' : `${Math.abs(item.pctChange)}%`;
    const directionLabel = item.isNew
      ? 'New'
      : isPositive
        ? `Up ${Math.abs(item.pctChange)}%`
        : isNegative
          ? `Down ${Math.abs(item.pctChange)}%`
          : 'No change';

    return (
      <div
        className="flex flex-col gap-1"
        data-testid="referral-volume-trends-preview-hero"
      >
        <span className="sr-only">{directionLabel}</span>
        <div className="flex items-baseline gap-3">
          <span
            className={`text-4xl font-bold tabular-nums ${arrowColor}`}
            data-testid="referral-volume-trends-preview-hero-arrow"
            aria-hidden="true"
          >
            {arrow}
          </span>
          <span
            className={`text-4xl font-bold tabular-nums ${arrowColor}`}
            data-testid="referral-volume-trends-preview-hero-value"
            aria-hidden="true"
          >
            {pctDisplay}
          </span>
        </div>
        <span
          className="text-sm font-semibold text-neutral-500"
          data-testid="referral-volume-trends-preview-hero-label"
        >
          {item.codeSetLabel}
        </span>
      </div>
    );
  }

  const displayItems = trendItems.slice(0, PREVIEW_MAX_ITEMS);

  return (
    <div
      className="flex flex-col gap-1.5"
      data-testid="referral-volume-trends-preview"
    >
      {displayItems.map((item) => (
        <PreviewYoYRow
          key={item.key}
          pctChange={item.pctChange}
          isNew={item.isNew}
          codeSetLabel={item.codeSetLabel}
        />
      ))}
      {trendItems.length > PREVIEW_MAX_ITEMS && (
        <span
          className="text-xs text-neutral-500"
          data-testid="referral-volume-trends-preview-overflow"
        >
          +{trendItems.length - PREVIEW_MAX_ITEMS} more
        </span>
      )}
    </div>
  );
};

export default ReferralVolumeTrendsPreview;
